import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { langPath } from '../../data/ladies';
import './Header.css';

const NAV_ITEMS = [
  { key: 'home', type: 'scroll', target: 'hero' },
  { key: 'rooms', type: 'link', target: '/rooms' },
  { key: 'ratesServices', type: 'link', target: '/rates' },
  { key: 'ladies', type: 'link', target: '/ladies' },
  { key: 'roster', type: 'link', target: '/roster' },
  { key: 'promos', type: 'scroll', target: 'promos' },
  { key: 'joinUs', type: 'link', target: '/join-us', className: 'header__nav-link--join' },
  { key: 'contact', type: 'scroll', target: 'contact' },
];

const SECTION_IDS = ['hero', 'services', 'rates', 'ladies', 'roster', 'promos', 'about', 'gallery', 'contact'];

const Header = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === '/' || location.pathname === '/en';
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('hero');

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 80);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    if (!isHome) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) setActiveSection(entry.target.id);
        });
      },
      { rootMargin: '-40% 0px -55% 0px' }
    );
    SECTION_IDS.forEach(id => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [isHome]);

  const goToSection = useCallback((id) => {
    setMenuOpen(false);

    if (isHome) {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
      return;
    }

    // Navigate back to the homepage first, then wait for the (lazy-loaded)
    // target section to mount before scrolling to it.
    const homePath = i18n.language === 'en' ? '/en' : '/';
    navigate(homePath);

    let attempts = 0;
    const tryScroll = () => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth' });
        return;
      }
      attempts += 1;
      if (attempts < 40) setTimeout(tryScroll, 50);
    };
    setTimeout(tryScroll, 50);
  }, [isHome, i18n.language, navigate]);

  const toggleLang = () => {
    const next = i18n.language === 'en' ? 'zh' : 'en';
    i18n.changeLanguage(next);
    localStorage.setItem('on_lang', next);
    const path = location.pathname;
    if (next === 'en' && path !== '/en' && !path.startsWith('/en/')) {
      navigate(path === '/' ? '/en' : `/en${path}`);
    } else if (next === 'zh' && path === '/en') {
      navigate('/');
    } else if (next === 'zh' && path.startsWith('/en/')) {
      navigate(path.slice(3));
    }
  };

  const isActive = (item) => {
    if (!isHome) return false;
    if (item.matchIds) return item.matchIds.includes(activeSection);
    return activeSection === item.target;
  };

  const isActiveLink = (path) => {
    const current = location.pathname;
    return current === path
      || current === `/en${path}`
      || (path !== '/' && (current.startsWith(`${path}/`) || current.startsWith(`/en${path}/`)));
  };

  return (
    <header className={`header ${scrolled ? 'header--scrolled' : ''} ${menuOpen ? 'header--open' : ''}`}>
      <div className="header__inner">
        <Link to={langPath('/', i18n.language)} className="header__brand" onClick={() => setMenuOpen(false)}>
          <span className="header__brand-name">OCEAN NOIR</span>
          <span className="header__brand-line" />
        </Link>

        <nav className={`header__nav ${menuOpen ? 'header__nav--visible' : ''}`}>
          {NAV_ITEMS.map((item) => (
            item.type === 'link' ? (
              <Link
                key={item.key}
                to={langPath(item.target, i18n.language)}
                className={`header__nav-link ${item.className || ''} ${isActiveLink(item.target) ? 'header__nav-link--active' : ''}`}
                onClick={() => setMenuOpen(false)}
              >
                {t(`nav.${item.key}`)}
              </Link>
            ) : (
              <button
                key={item.key}
                className={`header__nav-link ${isActive(item) ? 'header__nav-link--active' : ''}`}
                onClick={() => goToSection(item.target)}
              >
                {t(`nav.${item.key}`)}
              </button>
            )
          ))}
        </nav>

        <div className="header__controls">
          <button className="header__lang-btn" onClick={toggleLang} title="Switch language">
            {i18n.language === 'en' ? '中文' : 'EN'}
          </button>
          <button
            className={`header__hamburger ${menuOpen ? 'header__hamburger--open' : ''}`}
            onClick={() => setMenuOpen(v => !v)}
            aria-label="Toggle menu"
          >
            <span /><span /><span />
          </button>
        </div>
      </div>
    </header>
  );
};

export default Header;
